<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Icon from '../icon/Icon.vue';
import PolarisMark from '../sidebar/PolarisMark.vue';

defineProps({
  hasAssistants: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['useSuggestion']);
const { t } = useI18n();
const route = useRoute();

const routePromptMap = {
  conversations: [
    {
      label: 'POLARIS.COPILOT.PROMPTS.SUMMARIZE.LABEL',
      prompt: 'POLARIS.COPILOT.PROMPTS.SUMMARIZE.CONTENT',
    },
    {
      label: 'POLARIS.COPILOT.PROMPTS.SUGGEST.LABEL',
      prompt: 'POLARIS.COPILOT.PROMPTS.SUGGEST.CONTENT',
    },
    {
      label: 'POLARIS.COPILOT.PROMPTS.RATE.LABEL',
      prompt: 'POLARIS.COPILOT.PROMPTS.RATE.CONTENT',
    },
  ],
  dashboard: [
    {
      label: 'POLARIS.COPILOT.PROMPTS.HIGH_PRIORITY.LABEL',
      prompt: 'POLARIS.COPILOT.PROMPTS.HIGH_PRIORITY.CONTENT',
    },
    {
      label: 'POLARIS.COPILOT.PROMPTS.LIST_CONTACTS.LABEL',
      prompt: 'POLARIS.COPILOT.PROMPTS.LIST_CONTACTS.CONTENT',
    },
  ],
};

const getCurrentRoute = () => {
  const path = route.path;
  if (path.includes('/conversations')) return 'conversations';
  if (path.includes('/dashboard')) return 'dashboard';
  return 'dashboard';
};

const promptOptions = computed(() => {
  const currentRoute = getCurrentRoute();
  return routePromptMap[currentRoute] || routePromptMap.conversations;
});

const handleSuggestion = opt => {
  emit('useSuggestion', t(opt.prompt));
};
</script>

<template>
  <div class="flex-1 flex flex-col gap-6 px-2">
    <div class="flex flex-col space-y-4 py-4">
      <PolarisMark />
      <div class="space-y-1">
        <h3 class="type-polaris-section-title text-polaris-foreground">
          {{ $t('POLARIS.COPILOT.PANEL_TITLE') }}
        </h3>
        <p class="type-polaris-body text-polaris-muted-foreground">
          {{ $t('POLARIS.COPILOT.KICK_OFF_MESSAGE') }}
        </p>
      </div>
    </div>
    <div v-if="!hasAssistants" class="w-full space-y-2">
      <p class="text-sm text-polaris-muted-foreground leading-6">
        {{ $t('POLARIS.ASSISTANTS.NO_ASSISTANTS_AVAILABLE') }}
      </p>
      <router-link
        :to="{
          name: 'polaris_assistants_create_index',
          params: {
            accountId: route.params.accountId,
          },
        }"
        class="text-polaris-muted-foreground underline hover:text-polaris-foreground"
      >
        {{ $t('POLARIS.ASSISTANTS.ADD_NEW') }}
      </router-link>
    </div>
    <div v-else class="w-full space-y-2">
      <span class="block type-polaris-caption text-polaris-muted-foreground">
        {{ $t('POLARIS.COPILOT.TRY_THESE_PROMPTS') }}
      </span>
      <div class="space-y-1">
        <button
          v-for="prompt in promptOptions"
          :key="prompt.label"
          class="w-full px-3 py-2 rounded-polaris-md border border-solid border-polaris-border text-polaris-foreground flex items-center justify-between hover:bg-polaris-accent hover:text-polaris-accent-foreground transition-colors"
          @click="handleSuggestion(prompt)"
        >
          <span>{{ t(prompt.label) }}</span>
          <Icon icon="i-lucide-chevron-right" />
        </button>
      </div>
    </div>
  </div>
</template>
